import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ChatLoadErrorType } from '@/components/ChatLoadError';
import { useChatTransport } from '@/hooks/chat/useChatTransport';
import { useCanStartSupportThread } from '@/hooks/useSupportStaffAbilities';
import { chatTransportErrorKind, type ChatConversation } from '@/services/chat/chatTransport';
import {
  loadReadOnlyInquiryConversation,
  releaseReadOnlyInquiryConversation,
  type ReadOnlyInquiryConversation,
} from '@/services/chat/readOnlyInquiryConversation';
import { getInquiryV2QueryOptions, GetInquiryV2Response } from '@/services/getInquiryV2';
import { userIdAtom } from '@/services/globalState';
import { isApiErrorV2 } from '@/util/fetchClient';

export type InquiryParticipant = NonNullable<GetInquiryV2Response>['author'];

type ConversationError = {
  type: ChatLoadErrorType;
  message: string;
  isRefusedBeforeAbility?: boolean;
};

function getErrorTypeFromApiError(error: unknown): ChatLoadErrorType {
  if (isApiErrorV2(error)) {
    if (error.status === 403) {
      return 'forbidden';
    }

    if (error.status === 404) {
      return 'not_found';
    }
  }

  return 'unknown';
}

/**
 * Loads chat state for a conversation: the transport's conversation, inquiry (API), recipient,
 * and status. Status is taken from the inquiry API (`inquiryStatus`), not the transport.
 */
export function useChat(id: string) {
  const userId = useAtomValue(userIdAtom);

  const transport = useChatTransport();

  const canStartSupportThread = useCanStartSupportThread();

  const canStartSupportThreadRef = useRef(canStartSupportThread);

  useEffect(() => {
    canStartSupportThreadRef.current = canStartSupportThread;
  }, [canStartSupportThread]);

  const [transportConversation, setTransportConversation] = useState<ChatConversation | undefined>(
    undefined
  );

  const [readOnlyConversation, setReadOnlyConversation] = useState<
    ReadOnlyInquiryConversation | undefined
  >(undefined);

  const conversation = readOnlyConversation ?? transportConversation;

  const [conversationError, setConversationError] = useState<ConversationError | undefined>(
    undefined
  );

  const [isLoadingConversation, setIsLoadingConversation] = useState(true);

  const cancelLoadRef = useRef<() => void>(undefined);

  /**
   * Loads the conversation for `id`. Starting a load, from the effect or from
   * Retry, cancels the one before it, and so does the screen closing. A
   * cancelled load applies nothing, and lets go of a read-only conversation it
   * built by then, since that one is already polling and nothing else would.
   *
   * The ability is read from a ref rather than listed as a dependency, so its
   * rules arriving after the chat opened does not tear down and reload a
   * conversation that is already showing. A refusal that came before the rules
   * did is flagged, and reloaded once they arrive.
   */
  const loadConversation = useCallback(async () => {
    let isCancelled = false;

    cancelLoadRef.current?.();

    cancelLoadRef.current = () => {
      isCancelled = true;
    };

    if (!transport) {
      setIsLoadingConversation(false);

      return;
    }

    setTransportConversation(undefined);

    setReadOnlyConversation(undefined);

    setConversationError(undefined);

    setIsLoadingConversation(true);

    try {
      const conv = await transport.getConversation(id);

      if (isCancelled) {
        return;
      }

      setTransportConversation(conv);
    } catch (e) {
      if (isCancelled) {
        return;
      }

      const errorType = chatTransportErrorKind(e);

      const canReadAnyInquiry = canStartSupportThreadRef.current;

      /*
       * The transport refuses a reader who is not in the conversation. Primum Support
       * may still read any inquiry through our API, so it gets the messages
       * from there instead of an access error.
       */
      if (errorType === 'forbidden' && canReadAnyInquiry) {
        try {
          const readOnly = await loadReadOnlyInquiryConversation(id);

          if (isCancelled) {
            releaseReadOnlyInquiryConversation(readOnly);

            return;
          }

          setReadOnlyConversation(readOnly);

          return;
        } catch {
          if (isCancelled) {
            return;
          }
          // Fall through to the access error below.
        }
      }

      console.error('Failed to load conversation:', e);

      setConversationError({
        type: errorType,
        message: e instanceof Error ? e.message : 'Unknown error',
        isRefusedBeforeAbility: errorType === 'forbidden' && !canReadAnyInquiry,
      });
    } finally {
      if (!isCancelled) {
        setIsLoadingConversation(false);
      }
    }
  }, [transport, id]);

  useEffect(() => {
    // `loadConversation` handles its own errors, so this never rejects.
    loadConversation().catch(() => undefined);
  }, [loadConversation]);

  useEffect(() => () => cancelLoadRef.current?.(), []);

  useEffect(() => {
    if (canStartSupportThread && conversationError?.isRefusedBeforeAbility) {
      loadConversation().catch(() => undefined);
    }
  }, [canStartSupportThread, conversationError, loadConversation]);

  useEffect(
    () => () => {
      if (readOnlyConversation) {
        releaseReadOnlyInquiryConversation(readOnlyConversation);
      }
    },
    [readOnlyConversation]
  );

  const {
    data: inquiry,
    error: inquiryError,
    isLoading: isLoadingInquiry,
    refetch: refetchInquiry,
  } = useQuery({
    ...getInquiryV2QueryOptions({ conversationId: id }),
    enabled: !!transport,
  });

  const recipient = inquiry?.author?.id === Number(userId) ? inquiry?.recipient : inquiry?.author;

  const conversationStatus = inquiry?.inquiryStatus;

  const inquiryDerivedError: ConversationError | undefined = inquiryError
    ? {
        type: getErrorTypeFromApiError(inquiryError),
        message: isApiErrorV2(inquiryError) ? inquiryError.body.error : 'Unknown error',
      }
    : undefined;

  const error = conversationError ?? inquiryDerivedError;

  const isLoading = isLoadingConversation || isLoadingInquiry;

  const retry = useCallback(async () => {
    await loadConversation();

    await refetchInquiry();
  }, [loadConversation, refetchInquiry]);

  return {
    userId,
    conversation,
    recipient,
    conversationStatus,
    inquiry,
    error,
    isLoading,
    retry,
  };
}
